import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import api from "../services/api";
import { toastError, toastSuccess } from "../utils/hotToast";

export default function AdminDeletedExams() {
  const navigate = useNavigate();

  const [deletedExams, setDeletedExams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [restoringExamId, setRestoringExamId] = useState(null);

  const fetchDeletedExams = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/admin/exams/trash");
      setDeletedExams(res.data || []);
    } catch (err) {
      toastError(`Failed to load deleted exams: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDeletedExams();
  }, [fetchDeletedExams]);

  const filteredExams = useMemo(
    () =>
      deletedExams.filter((exam) =>
        (exam.title || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (exam.course || "").toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [deletedExams, searchTerm]
  );

  const handleRestore = async (exam) => {
    const ok = window.confirm(
      `Restore "${exam.title}"? It will reappear in the main exam list with all its copies intact.`
    );
    if (!ok) return;

    setRestoringExamId(exam._id);
    try {
      await api.patch(`/admin/exams/${exam._id}/restore`);
      toastSuccess(`"${exam.title}" restored successfully.`);
      fetchDeletedExams();
    } catch (err) {
      toastError(err.response?.data?.message || "Failed to restore exam.");
    } finally {
      setRestoringExamId(null);
    }
  };

  return (
    <div
      className="p-6 lg:p-10 space-y-6 bg-white min-h-screen"
      style={{ fontFamily: "Dosis, sans-serif" }}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-gray-900 rounded-xl">
              <TrashIcon className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
              Deleted Exams
            </h1>
          </div>
          <p className="text-sm text-gray-600 mt-1 ml-1">
            Exams moved to trash. All copies and Drive files are fully preserved — restore any exam to bring it back.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => navigate("/admin/completed-exams")}
            className="inline-flex items-center px-4 py-2 rounded-xl border-2 border-gray-900 text-gray-900 hover:bg-gray-100 transition font-semibold text-sm"
          >
            Completed Exams
          </button>
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center px-4 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 transition font-semibold text-sm"
          >
            Back to Admin Panel
          </button>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl px-5 py-3 flex items-start gap-3">
        <svg className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
        <p className="text-sm text-amber-800">
          Deleting an exam only hides it from the main panel — no copies or Drive files are ever permanently removed.
          Restore an exam at any time to make it fully active again.
        </p>
      </div>

      {/* Table card */}
      <div className="bg-white p-6 rounded-2xl border-2 border-gray-900">
        {/* Search + count */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
          <div className="relative w-full lg:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by title or course..."
              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          {!isLoading && (
            <span className="text-sm text-gray-500 font-medium">
              {filteredExams.length} exam{filteredExams.length !== 1 ? "s" : ""} in trash
            </span>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8">
            <ArrowPathIcon className="h-5 w-5 animate-spin" />
            <span>Loading deleted exams...</span>
          </div>
        ) : filteredExams.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4">
              <TrashIcon className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium text-lg">
              {searchTerm ? "No deleted exams match your search." : "Trash is empty."}
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Exams you delete from the main panel will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Course
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Exam Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deleted At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Examiners
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredExams.map((exam) => (
                  <tr key={exam._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {exam.title || "Untitled"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {exam.course || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {exam.date
                        ? new Date(exam.date).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {exam.deletedAt
                        ? new Date(exam.deletedAt).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "N/A"}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {exam.assignedExaminers?.length > 0 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {exam.assignedExaminers.length} assigned
                        </span>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleRestore(exam)}
                        disabled={restoringExamId === exam._id}
                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-white bg-amber-700 hover:bg-amber-800 disabled:opacity-50 transition font-medium"
                      >
                        {restoringExamId === exam._id ? (
                          <>
                            <ArrowPathIcon className="h-4 w-4 mr-1.5 animate-spin" />
                            Restoring...
                          </>
                        ) : (
                          <>
                            <ArrowUturnLeftIcon className="h-4 w-4 mr-1.5" />
                            Restore
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
